import { useState, useCallback, useEffect, useRef } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useUserId } from './useUserId'
import {
  UserPreferences,
  PersistentPreferences,
  SessionPreferences,
  DEFAULT_PERSISTENT_PREFERENCES,
  DEFAULT_SESSION_PREFERENCES
} from '../types/preferences'
import { backendApiService } from '../services/backendApiService'
import { clientLogger } from '../utils/clientLogger'

export function usePreferences() {
  const { isAuthenticated, isGuest, guestSession } = useAuth()
  const userId = useUserId() // Custom UUID architecture - works for both guest and authenticated users
  const [preferences, setPreferences] = useState<UserPreferences | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Stable ref to access latest preferences in stable callbacks
  const preferencesRef = useRef<UserPreferences | null>(null)
  preferencesRef.current = preferences

  // Track if we're currently loading to prevent duplicate requests
  const loadingRef = useRef(false)

  const loadPreferences = useCallback(async () => {
    // Prevent duplicate concurrent loads
    if (loadingRef.current) {
      clientLogger.debug({}, 'Already loading, skipping duplicate request')
      return
    }

    loadingRef.current = true
    setLoading(true)
    setError(null)

    try {
      if (!userId) {
        clientLogger.debug({}, 'No user ID available, no preferences to load')
        setPreferences(null)
        return
      }

      clientLogger.debug({ userId, isGuest }, 'Loading preferences for user')

      let loadedPrefs: UserPreferences | null = null

      try {
        if (isGuest && guestSession) {
          // Load guest preferences from backend
          const guestData = await backendApiService.loadGuestPreferences(guestSession.id)
          if (guestData) {
            // Check if persistent preferences have been customized (not just defaults)
            // A new user will have null/undefined primaryDietaryPreference
            const hasCustomizedPrefs = guestData.persistent?.primaryDietaryPreference !== null &&
                                       guestData.persistent?.primaryDietaryPreference !== undefined

            loadedPrefs = {
              persistent: guestData.persistent || DEFAULT_PERSISTENT_PREFERENCES,
              session: guestData.session,
              setup_completed: hasCustomizedPrefs,
              last_updated: guestData.created_at,
            }
            clientLogger.debug({ loadedPrefs }, 'Loaded guest preferences from backend')
          }
        } else if (isAuthenticated) {
          // Load authenticated user preferences from backend
          const [persistentData, sessionData] = await Promise.all([
            backendApiService.loadPersistentPreferences(userId).catch(() => null),
            backendApiService.loadSessionPreferences(userId).catch(() => null)
          ])

          if (persistentData) {
            loadedPrefs = {
              persistent: persistentData.persistent,
              session: sessionData,
              setup_completed: persistentData.setup_completed,
              last_updated: persistentData.last_updated,
            }
            clientLogger.debug({ loadedPrefs }, 'Loaded authenticated user preferences from backend')
          }
        }
      } catch (backendError) {
        clientLogger.warn({ err: backendError instanceof Error ? backendError : new Error(String(backendError)) }, 'Backend preference loading failed, falling back to localStorage')

        // Fallback to localStorage for offline/error scenarios
        const storageKey = `preferences_${userId}`
        const stored = localStorage.getItem(storageKey)
        if (stored) {
          try {
            loadedPrefs = JSON.parse(stored) as UserPreferences
            clientLogger.debug({ loadedPrefs }, 'Loaded preferences from localStorage fallback')
          } catch (parseError) {
            clientLogger.warn({ err: parseError instanceof Error ? parseError : new Error(String(parseError)) }, 'Failed to parse localStorage fallback')
          }
        }
      }

      // If no preferences loaded, create defaults
      if (!loadedPrefs) {
        clientLogger.debug({}, 'No preferences found, creating defaults')
        loadedPrefs = {
          persistent: DEFAULT_PERSISTENT_PREFERENCES,
          session: null,
          setup_completed: false,
          last_updated: new Date().toISOString(),
        }
      }

      setPreferences(loadedPrefs)
    } catch (err) {
      clientLogger.error({ err: err instanceof Error ? err : new Error(String(err)) }, 'Error loading preferences')
      setError('Failed to load preferences')
      // Set defaults even on error to prevent blank page
      setPreferences({
        persistent: DEFAULT_PERSISTENT_PREFERENCES,
        session: null,
        setup_completed: false,
        last_updated: new Date().toISOString(),
      })
    } finally {
      setLoading(false)
      loadingRef.current = false
    }
  }, [userId, isAuthenticated, isGuest, guestSession])

  // Load preferences on mount and auth changes
  useEffect(() => {
    loadPreferences()
  }, [loadPreferences])

  const updatePersistentPreferences = useCallback(async (persistent: PersistentPreferences) => {
    if (!preferences) return

    if (!userId) {
      clientLogger.error({}, 'No user ID available for saving persistent preferences')
      return
    }

    const updatedPrefs: UserPreferences = {
      ...preferences,
      persistent,
      setup_completed: true,
      last_updated: new Date().toISOString(),
    }

    // Update local state immediately (optimistic update)
    setPreferences(updatedPrefs)

    try {
      if (isGuest && guestSession) {
        // Save guest persistent preferences to backend
        await backendApiService.saveGuestPreferences(
          guestSession.id,
          persistent,
          preferences.session ?? undefined
        )
        clientLogger.debug({}, 'Saved guest persistent preferences to backend')
      } else if (isAuthenticated) {
        // Save authenticated user persistent preferences to backend
        await backendApiService.savePersistentPreferences(userId, persistent)
        clientLogger.debug({}, 'Saved authenticated user persistent preferences to backend')
      }

      // Also save to localStorage as backup
      const storageKey = `preferences_${userId}`
      localStorage.setItem(storageKey, JSON.stringify(updatedPrefs))
      clientLogger.debug({}, 'Saved persistent preferences to localStorage backup')
    } catch (error) {
      clientLogger.error({ err: error instanceof Error ? error : new Error(String(error)) }, 'Failed to save persistent preferences')

      // On backend failure, still save to localStorage
      try {
        const storageKey = `preferences_${userId}`
        localStorage.setItem(storageKey, JSON.stringify(updatedPrefs))
        clientLogger.debug({}, 'Saved persistent preferences to localStorage fallback')
      } catch (localError) {
        clientLogger.error({ err: localError instanceof Error ? localError : new Error(String(localError)) }, 'Failed to save to localStorage fallback')
      }
    }
  }, [preferences, userId, isGuest, isAuthenticated])

  const updateSessionPreferences = useCallback(async (session: SessionPreferences | null) => {
    if (!preferences) return

    if (!userId) {
      clientLogger.error({}, 'No user ID available for saving session preferences')
      return
    }

    // Always save the session preferences as-is for the current session
    const updatedPrefs: UserPreferences = {
      ...preferences,
      session,
      last_updated: new Date().toISOString(),
    }

    // Update local state immediately (optimistic update)
    setPreferences(updatedPrefs)

    try {
      if (isGuest && guestSession) {
        // Save guest session preferences to backend
        await backendApiService.saveGuestPreferences(
          guestSession.id,
          preferences.persistent,
          session ?? undefined
        )
        clientLogger.debug({}, 'Saved guest session preferences to backend')
      } else if (isAuthenticated) {
        // Save authenticated user session preferences to backend
        if (session) {
          await backendApiService.saveSessionPreferences(userId, session)
          clientLogger.debug({}, 'Saved authenticated user session preferences to backend')
        } else {
          await backendApiService.clearSessionPreferences(userId)
          clientLogger.debug({}, 'Cleared authenticated user session preferences from backend')
        }
      }

      // Also save to localStorage as backup
      const storageKey = `preferences_${userId}`
      localStorage.setItem(storageKey, JSON.stringify(updatedPrefs))
      clientLogger.debug({}, 'Saved session preferences to localStorage backup')
    } catch (error) {
      clientLogger.error({ err: error instanceof Error ? error : new Error(String(error)) }, 'Failed to save session preferences')

      // On backend failure, still save to localStorage
      try {
        const storageKey = `preferences_${userId}`
        localStorage.setItem(storageKey, JSON.stringify(updatedPrefs))
        clientLogger.debug({}, 'Saved session preferences to localStorage fallback')
      } catch (localError) {
        clientLogger.error({ err: localError instanceof Error ? localError : new Error(String(localError)) }, 'Failed to save to localStorage fallback')
        // If even localStorage fails, we should revert the optimistic update
        setError('Failed to save session preferences')
      }
    }
  }, [preferences, userId, isGuest, isAuthenticated])

  const clearSessionPreferences = useCallback(() => {
    if (!preferences) return

    const updatedPrefs: UserPreferences = {
      ...preferences,
      session: null,
      last_updated: new Date().toISOString(),
    }

    setPreferences(updatedPrefs)
  }, [preferences])

  const isSetupComplete = useCallback(() => {
    return preferences?.setup_completed === true
  }, [preferences])

  const needsInitialSetup = useCallback(() => {
    return !isSetupComplete()
  }, [preferences])

  const getPersistentPreferences = useCallback((): PersistentPreferences => {
    return preferencesRef.current?.persistent || DEFAULT_PERSISTENT_PREFERENCES
  }, [])

  const getSessionPreferences = useCallback((): SessionPreferences => {
    const result = preferences?.session || DEFAULT_SESSION_PREFERENCES
    clientLogger.debug({ hasPreferences: !!preferences, hasSession: !!preferences?.session, resultMealCounts: result.mealCounts, resultNumberOfPeople: result.numberOfPeople }, 'getSessionPreferences called')
    return result
  }, [preferences])

  const hasRememberedSessionPrefs = useCallback((): boolean => {
    return preferences?.session?.rememberSettings === true
  }, [preferences])

  const getCoreSessionPreferencesForRemembering = useCallback((session: SessionPreferences): SessionPreferences => {
    // Return session preferences excluding cuisinePreferences for remembering
    return {
      ...session,
      cuisinePreferences: [], // Reset cuisine to empty for remembered preferences
    }
  }, [])

  const getSessionPreferencesForNewSession = useCallback((): SessionPreferences => {
    const currentPreferences = preferencesRef.current
    clientLogger.debug({ hasSession: !!currentPreferences?.session }, 'getSessionPreferencesForNewSession called')

    // Check if remember settings is enabled
    if (currentPreferences?.session?.rememberSettings) {
      // Remember is enabled, use core preferences with empty cuisine
      const corePrefs = getCoreSessionPreferencesForRemembering(currentPreferences.session)
      clientLogger.debug({}, 'Remember enabled, using core preferences')
      return corePrefs
    }

    // Remember is disabled or no session preferences exist - always return defaults
    clientLogger.debug({}, 'Remember disabled or no session preferences, using defaults')
    return DEFAULT_SESSION_PREFERENCES
  }, [getCoreSessionPreferencesForRemembering])

  return {
    // State
    preferences,
    loading,
    error,
    
    // Actions
    updatePersistentPreferences,
    updateSessionPreferences,
    clearSessionPreferences,
    
    // Getters
    getPersistentPreferences,
    getSessionPreferences,
    getSessionPreferencesForNewSession,
    
    // Status checks
    isSetupComplete,
    needsInitialSetup,
    hasRememberedSessionPrefs,
  }
}